import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { PRODUCTION_URL } from '../pages/productionLink/productionLink';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // API Base URL - Update this according to your setup
  const API_BASE_URL = process.env.REACT_APP_API_URL || `${PRODUCTION_URL}/api`;

  // ============================================
  // MENU TO PATH MAPPING
  // ============================================
  const menuToPathMap = {
    'Dashboard': '/admin/dashboard',
    'Stay Overview': '/admin/stay-overview',
    'Front Office': '/admin/front-office/operations',
    'House Keeping': '/admin/house-keeping/operations',
    'Point Of Sale': '/admin/pos/dashboard',
    'Kitchen & Restaurant': '/admin/kitchen/operations',
    'Bar Management': '/admin/bar/main',
    'Banquet': '/admin/banquet/operations',
    'Stores': '/admin/stores/operations',
    'Accounts': '/admin/accounts/operations',
    'Channel Manager': '/admin/channel-manager/operations',
    'Setup': '/admin/setup/system',
    'Administration': '/admin/administration/system'
  };

  // ============================================
  // GET DEFAULT REDIRECT PATH BASED ON USER ROLE
  // ============================================
  const getDefaultRedirectPath = (allowedMenus) => {
    // If no allowed menus or empty, default to dashboard
    if (!allowedMenus || allowedMenus.length === 0) {
      return '/admin/dashboard';
    }

    // Get the first allowed menu and return its path
    const firstMenu = allowedMenus[0];
    return menuToPathMap[firstMenu] || '/admin/dashboard';
  };

  // ============================================
  // VERIFY TOKEN ON APP LOAD
  // ============================================
  const verifyToken = useCallback(async () => {
    const token = localStorage.getItem('token');

    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/auth/verify`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (data.success && data.user) {
        // Set user with all role information and property data
        const userData = {
          id: data.user.id,
          username: data.user.username,
          fullName: data.user.fullName || data.user.username,
          email: data.user.email,
          role: data.user.role,
          roleCode: data.user.roleCode,
          roleName: data.user.roleName,
          allowedMenus: data.user.allowedMenus || [],
          // Multi-tenant property information (from nested object)
          propertyId: data.user.property?.id,
          propertyCode: data.user.property?.code,
          propertyName: data.user.property?.name
        };
        setUser(userData);
      } else {
        // Invalid token - clear storage
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setUser(null);
      }
    } catch (err) {
      console.error('Token verification error:', err);
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [API_BASE_URL]);

  useEffect(() => {
    verifyToken();
  }, [verifyToken]);

  // ============================================
  // LOGIN FUNCTION
  // ============================================
  const login = async (username, password) => {
    setError(null);
    setLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ username, password })
      });

      const data = await response.json();

      if (data.success) {
        // Store token
        localStorage.setItem('token', data.token);

        // Create user object with role information and property data
        const userData = {
          id: data.user.id,
          username: data.user.username,
          fullName: data.user.fullName || data.user.username,
          email: data.user.email,
          role: data.user.role,
          roleCode: data.user.roleCode,
          roleName: data.user.roleName,
          allowedMenus: data.user.allowedMenus || [],
          // Multi-tenant property information (from nested object)
          propertyId: data.user.property?.id,
          propertyCode: data.user.property?.code,
          propertyName: data.user.property?.name
        };

        // Store user data
        localStorage.setItem('user', JSON.stringify(userData));
        setUser(userData);

        // Get the redirect path based on allowed menus
        const redirectPath = getDefaultRedirectPath(userData.allowedMenus);

        return { success: true, user: userData, redirectPath };
      } else {
        setError(data.message || 'Login failed');
        return { success: false, message: data.message };
      }
    } catch (err) {
      console.error('Login error:', err);
      const errorMsg = 'Unable to connect to server';
      setError(errorMsg);
      return { success: false, message: errorMsg };
    } finally {
      setLoading(false);
    }
  };

  // ============================================
  // LOGOUT FUNCTION
  // ============================================
  const logout = async () => {
    try {
      const token = localStorage.getItem('token');

      // Call logout API (optional)
      if (token) {
        await fetch(`${API_BASE_URL}/auth/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }).catch(() => { }); // Ignore errors
      }
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      // Clear local storage
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      setUser(null);
      setError(null);
    }
  };

  // ============================================
  // CHECK IF USER HAS PERMISSION FOR A MENU
  // ============================================
  const hasPermission = (menuLabel) => {
    if (!user) return false;

    // Admin has all permissions
    if (user.roleCode === 'admin') return true;

    // Check if menu is in allowed menus
    return user.allowedMenus?.includes(menuLabel) || false;
  };

  // ============================================
  // CHECK IF USER HAS PERMISSION FOR A PATH
  // ============================================
  const hasPathPermission = (path) => {
    if (!user) return false;

    // Admin has all permissions
    if (user.roleCode === 'admin') return true;

    // Map paths to menu labels
    const pathToMenuMap = {
      '/admin/dashboard': 'Dashboard',
      '/admin/stay-overview': 'Stay Overview',
      '/admin/front-office': 'Front Office',
      '/admin/house-keeping': 'House Keeping',
      '/admin/pos': 'Point Of Sale',
      '/admin/kitchen': 'Kitchen & Restaurant',
      '/admin/bar': 'Bar Management',
      '/admin/banquet': 'Banquet',
      '/admin/stores': 'Stores',
      '/admin/accounts': 'Accounts',
      '/admin/profit-loss': 'Accounts',
      '/admin/channel-manager': 'Channel Manager',
      '/admin/setup': 'Setup',
      '/admin/administration': 'Administration'
    };

    // Find matching menu label
    for (const [pathPrefix, menuLabel] of Object.entries(pathToMenuMap)) {
      if (path.startsWith(pathPrefix)) {
        return user.allowedMenus?.includes(menuLabel) || false;
      }
    }

    return false;
  };

  // ============================================
  // GET USER'S ROLE DISPLAY NAME
  // ============================================
  const getRoleDisplayName = () => {
    if (!user) return 'Guest';
    return user.roleName || 'User';
  };

  // ============================================
  // GET USER'S DEFAULT REDIRECT PATH
  // ============================================
  const getUserDefaultPath = () => {
    if (!user) return '/login';
    if (user.roleCode === 'admin') return '/admin/dashboard';
    return getDefaultRedirectPath(user.allowedMenus);
  };

  const value = {
    user,
    loading,
    error,
    login,
    logout,
    hasPermission,
    hasPathPermission,
    getRoleDisplayName,
    getUserDefaultPath,
    getDefaultRedirectPath,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;